import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import useAuth from 'hooks/useAuth';
import { autoLogOut } from '../../services/checkTokenExpiry';
import { DecryptJweToken } from '../../services/EncDecService';
import { jwtDecode } from 'jwt-decode';
import { sessionDataDec } from '../../services/sessionServiceEndDec';
import navigation from 'menu-items'; // same import Breadcrumbs uses
import { RoleCheck } from '../../hooks/roleCheck';

// ── Recursively find the nav item matching the current path ──
function findNavItem(items, currentPath) {
  for (const menu of items) {
    if (menu.type === 'group' && menu.children) {
      for (const collapse of menu.children) {

        if (collapse.type === 'item' && collapse.url === currentPath) {
          return collapse;
        }

        if (collapse.type === 'collapse' && collapse.children) {
          for (const item of collapse.children) {
            if (item.type === 'item') {

              // Direct URL match
              if (item.url === currentPath) return item;

              // Check subRoutes (matches /bulk-customer/report etc.)
              if (item.subRoutes?.length) {
                const matched = findInSubRoutes(item.subRoutes, currentPath);
                if (matched) return item; // role guard is on the parent nav item
              }
            }
          }
        }

      }
    }
  }
  return null;
}

function findInSubRoutes(subRoutes, currentPath) {
  for (const sub of subRoutes) {
    if (sub.url === currentPath) return sub;
    if (sub.subRoutes?.length) {
      const deep = findInSubRoutes(sub.subRoutes, currentPath);
      if (deep) return deep;
    }
  }
  return null;
}

// ==============================|| AUTH GUARD ||============================== //

export default function AuthGuard({ children }) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyToken = async () => {
      const token = sessionDataDec('token');
      if (!token) {
        navigate('/login', { state: { from: '' }, replace: true });
        window.history.pushState(null, "", window.location.href);
        return;
      }

      const jweToken = await DecryptJweToken(token);
      const decoded = jwtDecode(jweToken);
      const exp = decoded.exp * 1000;
      const now = new Date().getTime();

      if (exp <= now) {
        await autoLogOut();
        return;
      }

      if (!isLoggedIn) {
        navigate('/login', { state: { from: '' }, replace: true });
        return;
      }

      // ── Auto role check using same nav tree as Breadcrumbs ──
      const matchedItem = findNavItem(navigation.items, location.pathname);
      if (matchedItem && !RoleCheck(matchedItem)) {
        navigate('/unauthorized', { replace: true });
        return;
      }
    };

    verifyToken();
  }, [isLoggedIn, navigate, location.pathname]);

  return children;
}

AuthGuard.propTypes = { children: PropTypes.any };